<!--
  LoginView.vue — the login screen (public route /login).
  Purpose: the email+password form that dispatches the auth engine's `user/auth`
  action, mirroring login-form (AUTH-SESSION §1.2/§1.3). On success it honours a
  `?redirect=` query (external URLs via window.location, internal via router), else
  pushes '/'. Server errors are surfaced from status.code (7 → field errors, 201 →
  email-not-found, else common.errors.<code> / login.error) as processServerErrors does.
  Validation matches the deployed form: login required+email, password 6..20.
  Reconstructed from lacak.io deployed behavior; app-specific contract verbatim
  (action `user/auth`, params {login,password,dealer_id,locale}, codes 7/201/103,
  redirect handling — AUTH-SESSION §1), internal impl re-authored.
-->
<template>
  <v-container fill-height class="tracko-center">
    <v-layout align-center justify-center>
      <v-flex xs12 sm8 md5 lg4>
        <v-card class="pa-3">
          <v-card-title primary-title>
            <div class="headline">{{ $t('login.title') }}</div>
          </v-card-title>

          <v-card-text>
            <v-alert :value="!!formError" type="error" outline dismissible @input="formError = ''">
              {{ formError }}
            </v-alert>

            <v-text-field
              v-model.trim="login"
              :label="$t('login.email')"
              type="email"
              prepend-icon="email"
              :error-messages="loginErrors"
              @keyup.enter="submit"
            ></v-text-field>

            <v-text-field
              v-model="password"
              :label="$t('login.password')"
              :type="passHidden ? 'password' : 'text'"
              prepend-icon="lock"
              :append-icon="passHidden ? 'visibility' : 'visibility_off'"
              :error-messages="passwordErrors"
              @click:append="passHidden = !passHidden"
              @keyup.enter="submit"
            ></v-text-field>
          </v-card-text>

          <v-card-actions>
            <v-btn flat small :to="{ name: 'restore' }">{{ $t('login.forgot_password') }}</v-btn>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              :loading="loading"
              :disabled="loading || invalid"
              @click="submit"
            >{{ $t('login.log_in') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
export default {
  name: 'LoginView',
  data() {
    return {
      login: '',
      password: '',
      passHidden: true,
      loading: false,
      // serverErrors bucket keyed like processServerErrors: {code, login, password}
      serverErrors: { code: [], login: [], password: [] },
      formError: ''
    }
  },
  computed: {
    // Vuelidate-equivalents: login required+email, password required 6..20.
    loginValid() {
      return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(this.login)
    },
    passwordValid() {
      return this.password.length >= 6 && this.password.length <= 20
    },
    invalid() {
      return !this.loginValid || !this.passwordValid
    },
    loginErrors() {
      return this.serverErrors.login.concat(this.serverErrors.code)
    },
    passwordErrors() {
      return this.serverErrors.password
    }
  },
  methods: {
    resetErrors() {
      this.serverErrors = { code: [], login: [], password: [] }
      this.formError = ''
    },
    async submit() {
      if (this.invalid || this.loading) return
      this.resetErrors()
      this.loading = true
      const params = {
        login: this.login,
        password: this.password,
        // dealer_id normally comes from paasUser/settings.paas_info.id; unknown in
        // the bare shell, so omit-or-undefined — the engine/backend defaults it.
        dealer_id: this.dealerId(),
        locale: this.$i18n.locale
      }
      try {
        // Dispatch the auth engine's action. It sets the session_key cookie and
        // loads the user (AUTH-SESSION §1.4). If the engine isn't wired this throws
        // "unknown action type: user/auth" and we show a clear message.
        await this.$store.dispatch('user/auth', params)
        this.afterLogin()
      } catch (err) {
        this.processServerErrors(err)
      } finally {
        this.loading = false
      }
    },
    dealerId() {
      const key = 'paasUser/settings'
      const g = this.$store && this.$store.getters
      const settings = g && g[key]
      return settings && settings.paas_info ? settings.paas_info.id : undefined
    },
    afterLogin() {
      // loginAction(): honour ?redirect (external via location, internal via router),
      // else go home. (AUTH-SESSION §1.3)
      const redirect = this.$route.query.redirect
      if (redirect) {
        if (/^http/.test(redirect)) {
          window.location.href = decodeURIComponent(redirect)
        } else {
          this.$router.push(redirect)
        }
      } else {
        this.$router.push('/')
      }
    },
    processServerErrors(err) {
      // If the auth engine isn't present the dispatch rejects with a Vuex error
      // rather than an axios response — show a helpful message.
      const response = err && err.response
      if (!response || !response.data || !response.data.status) {
        this.formError =
          err && /unknown action type/.test(String(err.message || ''))
            ? 'Auth engine (src/api) is not wired yet — see BUILD.md "Engine contract".'
            : this.$t('login.error')
        return
      }
      const status = response.data.status
      const code = status.code
      if (code === 7 && Array.isArray(status.errors)) {
        // field-level: map errors[].parameter → serverErrors[param]
        status.errors.forEach((e) => {
          const bucket = this.serverErrors[e.parameter] || this.serverErrors.code
          bucket.push(e.error || this.$t('login.error'))
        })
      } else if (code === 201) {
        this.serverErrors.code = [this.$t('login.email_not_found')]
      } else {
        const localized = this.$t('common.errors.' + code)
        this.serverErrors.code = [
          localized && localized !== 'common.errors.' + code ? localized : this.$t('login.error')
        ]
      }
    }
  }
}
</script>
